/**
 * Formulaire de création/édition d'un bundle
 * Permet de configurer les produits principaux, upsell et downsell
 */

import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  Card,
  Row,
  Col,
  Space,
  Divider,
  Table,
  Modal,
  Tag,
  Upload,
  DatePicker,
  message
} from 'antd';
import {
  PlusIcon,
  TrashIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PhotoIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { productsApi } from '../../utils/api';
import bundleService from '../../services/bundleService';
import { getDeviseActive } from '../../utils/devise';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const BundleForm = ({ bundle, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [bundleItems, setBundleItems] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [productVariants, setProductVariants] = useState({});

  // Initialiser le formulaire avec les données du bundle (mode édition)
  useEffect(() => {
    if (bundle) {
      form.setFieldsValue({
        name: bundle.name,
        description: bundle.description,
        bundle_type: bundle.bundle_type,
        status: bundle.status,
        base_price: bundle.base_price,
        discount_type: bundle.discount_type,
        discount_value: bundle.discount_value,
        min_quantity: bundle.min_quantity,
        max_quantity: bundle.max_quantity,
        priority: bundle.priority,
        tags: bundle.tags
      });

      // Charger les articles du bundle
      if (bundle.bundle_items) {
        setBundleItems(bundle.bundle_items.map(item => ({
          key: item.id || Math.random().toString(),
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          product_name: item.products?.name || 'Produit supprimé',
          variant_title: item.product_variants?.title || 'Variante par défaut',
          quantity: item.quantity,
          item_type: item.item_type,
          discount_percentage: item.discount_percentage,
          is_required: item.is_required,
          sort_order: item.sort_order,
          price: item.product_variants?.price || item.products?.price || 0
        })));
      }
    } else {
      // Mode création - valeurs par défaut
      form.setFieldsValue({
        bundle_type: 'standard',
        status: 'draft',
        discount_type: 'percentage',
        discount_value: 0,
        min_quantity: 1,
        priority: 0
      });
    }
  }, [bundle, form]);

  // Charger la liste des produits au montage
  useEffect(() => {
    loadProducts();
  }, []);

  // Recalculer le prix quand les articles changent
  useEffect(() => {
    calculateBundlePrice();
  }, [bundleItems]);

  /**
   * Charger la liste des produits disponibles
   */
  const loadProducts = async () => {
    try {
      const response = await productsApi.getAll();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      message.error('Erreur lors du chargement des produits');
    }
  };

  /**
   * Charger les variantes d'un produit
   */
  const loadProductVariants = async (productId) => {
    try {
      // Simuler le chargement des variantes
      // En réalité, on devrait avoir un endpoint pour récupérer les variantes d'un produit
      const product = products.find(p => p.id === productId);
      if (product && product.product_variants) {
        setProductVariants(prev => ({
          ...prev,
          [productId]: product.product_variants
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des variantes:', error);
    }
  };

  /**
   * Calculer le prix total du bundle
   */
  const calculateBundlePrice = () => {
    let total = 0;
    
    bundleItems.forEach(item => {
      if (item.item_type === 'main' || !item.item_type) {
        let itemPrice = item.price || 0;
        
        // Appliquer la réduction spécifique à l'article
        if (item.discount_percentage > 0) {
          itemPrice = itemPrice * (1 - item.discount_percentage / 100);
        }
        
        total += itemPrice * (item.quantity || 1);
      }
    });

    // Appliquer la réduction globale du bundle
    const discountType = form.getFieldValue('discount_type');
    const discountValue = form.getFieldValue('discount_value') || 0;
    
    if (discountType === 'percentage' && discountValue > 0) {
      total = total * (1 - discountValue / 100);
    } else if (discountType === 'fixed_amount' && discountValue > 0) {
      total = Math.max(total - discountValue, 0);
    }

    setCalculatedPrice(total);
  };

  /**
   * Ajouter un produit au bundle
   */
  const handleAddProduct = (productData) => {
    const newItem = {
      key: Math.random().toString(),
      product_id: productData.product_id,
      product_variant_id: productData.product_variant_id,
      product_name: productData.product_name,
      variant_title: productData.variant_title || 'Variante par défaut',
      quantity: productData.quantity || 1,
      item_type: productData.item_type || 'main',
      discount_percentage: productData.discount_percentage || 0,
      is_required: productData.is_required !== false,
      sort_order: bundleItems.length,
      price: productData.price || 0
    };

    setBundleItems(prev => [...prev, newItem]);
    setShowProductSelector(false);
  };

  /**
   * Supprimer un produit du bundle
   */
  const handleRemoveProduct = (key) => {
    setBundleItems(prev => prev.filter(item => item.key !== key));
  };

  /**
   * Mettre à jour un article du bundle
   */
  const handleUpdateItem = (key, field, value) => {
    setBundleItems(prev => prev.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async (values) => {
    try {
      // Vérifier qu'il y a au moins un article principal
      const mainItems = bundleItems.filter(item => item.item_type === 'main' || !item.item_type);
      if (mainItems.length === 0) {
        message.error('Vous devez ajouter au moins un article principal au bundle');
        return;
      }

      setLoading(true);

      const bundleData = {
        ...values,
        base_price: calculatedPrice,
        items: bundleItems.map((item, index) => ({
          product_id: item.product_id,
          product_variant_id: item.product_variant_id || null,
          quantity: item.quantity,
          item_type: item.item_type,
          discount_percentage: item.discount_percentage,
          is_required: item.is_required,
          sort_order: index
        }))
      };

      await onSave(bundleData);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde du bundle');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Colonnes de la table des articles du bundle
   */
  const itemColumns = [
    {
      title: 'Produit',
      key: 'product',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.product_name}</div>
          <div className="text-sm text-gray-500">{record.variant_title}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      key: 'item_type',
      render: (type) => {
        const colors = {
          main: 'blue',
          upsell: 'green',
          downsell: 'orange'
        };
        const icons = {
          main: null,
          upsell: <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />,
          downsell: <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
        };
        return (
          <Tag color={colors[type]} icon={icons[type]}>
            {type === 'main' ? 'Principal' : type === 'upsell' ? 'Upsell' : 'Downsell'}
          </Tag>
        );
      },
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(val) => handleUpdateItem(record.key, 'quantity', val)}
          size="small"
        />
      ),
    },
    {
      title: 'Réduction (%)',
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          max={100}
          value={value}
          onChange={(val) => handleUpdateItem(record.key, 'discount_percentage', val)}
          size="small"
          formatter={value => `${value}%`}
          parser={value => value.replace('%', '')}
        />
      ),
    },
    {
      title: 'Prix',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => {
        const discountedPrice = price * (1 - (record.discount_percentage || 0) / 100);
        return (
          <div className="text-right">
            {record.discount_percentage > 0 && (
              <div className="text-sm text-gray-400 line-through">
                {new Intl.NumberFormat('fr-FR').format(price)} {getDeviseActive()}
              </div>
            )}
            <div className="font-medium">
              {new Intl.NumberFormat('fr-FR').format(discountedPrice)} {getDeviseActive()}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button 
          type="text" 
          danger 
          icon={<TrashIcon className="h-4 w-4" />}
          onClick={() => handleRemoveProduct(record.key)}
        />
      ),
    },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        {/* Informations générales */}
        <Card title="Informations générales" className="mb-6">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Nom du bundle"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Ex: Pack déjeuner complet" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="bundle_type"
                label="Type de bundle"
                rules={[{ required: true, message: 'Le type est requis' }]}
              >
                <Select>
                  <Option value="standard">Standard</Option>
                  <Option value="promotional">Promotionnel</Option>
                  <Option value="seasonal">Saisonnier</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea 
              rows={3} 
              placeholder="Description du bundle pour vos clients" 
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="status"
                label="Statut"
              >
                <Select>
                  <Option value="active">Actif</Option>
                  <Option value="inactive">Inactif</Option>
                  <Option value="draft">Brouillon</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="min_quantity"
                label="Quantité minimale"
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="max_quantity"
                label="Quantité maximale"
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Réductions */}
        <Card title="Configuration des réductions" className="mb-6">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="discount_type"
                label="Type de réduction"
              >
                <Select onChange={calculateBundlePrice}>
                  <Option value="percentage">Pourcentage</Option>
                  <Option value="fixed_amount">Montant fixe</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="discount_value"
                label="Valeur de la réduction"
              >
                <InputNumber 
                  min={0}
                  style={{ width: '100%' }}
                  onChange={calculateBundlePrice}
                  formatter={value => {
                    const discountType = form.getFieldValue('discount_type');
                    return discountType === 'percentage' ? `${value}%` : `${value} ${getDeviseActive()}`;
                  }}
                  parser={value => value.replace(/[^\d]/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="priority"
                label="Priorité d'affichage"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-lg font-medium text-blue-900">
              Prix calculé du bundle : {new Intl.NumberFormat('fr-FR').format(calculatedPrice)} {getDeviseActive()}
            </div>
            <div className="text-sm text-blue-700 mt-1">
              Ce prix est calculé automatiquement en fonction des articles et réductions configurés
            </div>
          </div>
        </Card>

        {/* Articles du bundle */}
        <Card 
          title="Articles du bundle" 
          className="mb-6"
          extra={
            <Button
              type="primary"
              icon={<PlusIcon className="h-4 w-4" />}
              onClick={() => setShowProductSelector(true)}
            >
              Ajouter un produit
            </Button>
          }
        >
          {bundleItems.length > 0 ? (
            <Table
              columns={itemColumns}
              dataSource={bundleItems}
              pagination={false}
              size="small"
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Squares2X2Icon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Aucun produit ajouté</p>
              <p className="text-sm">Cliquez sur "Ajouter un produit" pour commencer</p>
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button onClick={onCancel}>
            Annuler
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {bundle ? 'Mettre à jour' : 'Créer'} le bundle
          </Button>
        </div>
      </Form>

      {/* Modal de sélection de produit */}
      <ProductSelector
        visible={showProductSelector}
        products={products}
        onSelect={handleAddProduct}
        onCancel={() => setShowProductSelector(false)}
      />
    </div>
  );
};

/**
 * Composant de sélection de produit pour le bundle
 */
const ProductSelector = ({ visible, products, onSelect, onCancel }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [itemType, setItemType] = useState('main');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isRequired, setIsRequired] = useState(true);

  const handleConfirm = () => {
    if (!selectedProduct) {
      message.error('Veuillez sélectionner un produit');
      return;
    }

    const productData = {
      product_id: selectedProduct.id,
      product_variant_id: selectedVariant?.id || null,
      product_name: selectedProduct.name,
      variant_title: selectedVariant?.title || null,
      quantity,
      item_type: itemType,
      discount_percentage: discountPercentage,
      is_required: isRequired,
      price: selectedVariant?.price || selectedProduct.price || 0
    };

    onSelect(productData);
    
    // Reset
    setSelectedProduct(null);
    setSelectedVariant(null);
    setQuantity(1);
    setItemType('main');
    setDiscountPercentage(0);
    setIsRequired(true);
  };

  return (
    <Modal
      title="Ajouter un produit au bundle"
      open={visible}
      onOk={handleConfirm}
      onCancel={onCancel}
      width={600}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Produit</label>
          <Select
            showSearch
            placeholder="Rechercher et sélectionner un produit"
            value={selectedProduct?.id}
            onChange={(value) => {
              const product = products.find(p => p.id === value);
              setSelectedProduct(product);
              setSelectedVariant(null);
            }}
            style={{ width: '100%' }}
            filterOption={(input, option) =>{
              console.log('option', option);
              console.log('input', input);
              return option.children[0].toLowerCase().includes(input.toLowerCase());
            }
            }
          >
            {products.map(product => (
              <Option key={product.id} value={product.id}>
                {product.name} - {new Intl.NumberFormat('fr-FR').format(product.price || 0)} {getDeviseActive()}
              </Option>
            ))}
          </Select>
        </div>

        {selectedProduct?.product_variants?.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">Variante (optionnel)</label>
            <Select
              placeholder="Laisser vide pour permettre le choix libre"
              value={selectedVariant?.id}
              onChange={(value) => {
                const variant = selectedProduct.product_variants.find(v => v.id === value);
                setSelectedVariant(variant);
              }}
              style={{ width: '100%' }}
              allowClear
            >
              {selectedProduct.product_variants.map(variant => (
                <Option key={variant.id} value={variant.id}>
                  {variant.title} - {new Intl.NumberFormat('fr-FR').format(variant.price || 0)} {getDeviseActive()}
                </Option>
              ))}
            </Select>
          </div>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <label className="block text-sm font-medium mb-2">Quantité</label>
            <InputNumber
              min={1}
              value={quantity}
              onChange={setQuantity}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <label className="block text-sm font-medium mb-2">Type d'article</label>
            <Select
              value={itemType}
              onChange={setItemType}
              style={{ width: '100%' }}
            >
              <Option value="main">Principal</Option>
              <Option value="upsell">Upsell</Option>
              <Option value="downsell">Downsell</Option>
            </Select>
          </Col>
        </Row>

        <div>
          <label className="block text-sm font-medium mb-2">Réduction spécifique (%)</label>
          <InputNumber
            min={0}
            max={100}
            value={discountPercentage}
            onChange={setDiscountPercentage}
            style={{ width: '100%' }}
            formatter={value => `${value}%`}
            parser={value => value.replace('%', '')}
          />
        </div>

        <div>
          <label className="flex items-center">
            <Switch
              checked={isRequired}
              onChange={setIsRequired}
              size="small"
            />
            <span className="ml-2 text-sm">Article obligatoire dans le bundle</span>
          </label>
        </div>
      </div>
    </Modal>
  );
};

export default BundleForm;